/**
 * El catálogo de precios: what each product sells for on the lists chosen, by category, as a PDF to
 * send or print (the owner, 2026-10-19: «a function in the products page to make a catalog of
 * prices both wholesale and retail as a pdf that you can download»). Selling prices only: no
 * cost, no stock, nothing a customer should not be handed.
 */
import { formatMoney, type Centavos } from '@abastece/shared';
import { precioId, ultimoDiaDeOferta, type ListaPrecio, type Oferta, type Producto, type UnidadVenta } from '@abastece/core';
import { unidadesDe } from './catalogo.js';
import { estadoDeOferta } from './ofertas.js';
import { DocumentoPdf, recortar, type Jpeg } from './pdf.js';

/** One presentation of one product, and its price on each list, in the lists' order. */
export interface FilaDelCatalogo {
  productoId: string;
  producto: string;
  presentacion: string;
  precios: (Centavos | null)[];
  /** The product's photo, by its name (`Producto.foto`), on each of its rows. */
  foto?: string | null;
  /** Under the name, smaller: an offer's price before it and its last day (2026-11-03). */
  nota?: string;
}

export interface GrupoDelCatalogo {
  categoria: string;
  filas: FilaDelCatalogo[];
}

/** The offers running on the day, for the catalogue's first group, and how it says them. */
export interface OfertasDelCatalogo {
  ofertas: readonly Oferta[];
  hoy: string;
  /** The group's name: «Ofertas». */
  titulo: string;
  /** Under a row's name: the list's price before it, when it has one, and its last day. */
  nota: (antes: Centavos | null, hasta: string) => string;
  /** Under the name of a row at so many for fewer: «2×1», what so many come to, and its last day. */
  notaLleva: (lleva: number, paga: number, porTodas: Centavos, hasta: string) => string;
}

/**
 * The products on sale with a price on at least one of `listas`, by category, each category and
 * each product in alphabetical order, a row for each of its presentations priced there, smallest
 * first: the packet, then the box of 12. A presentation priced on none of them is left out, and a
 * product with none left goes too.
 *
 * With `conOfertas`, the offers running that day on one of `listas` go first, a group of their own
 * (2026-11-03): each presentation an offer names, at its price on its list's column and nothing on
 * the others, the list's price before it and its last day under the name. One at so many for fewer
 * at the list's price, which it keeps, «2×1, lleva 2 por C$ 30.00» under the name. Their products
 * stay in their categories, at the list's own prices, which are theirs again once the offer ends.
 */
export function gruposDelCatalogo(
  productos: readonly Producto[],
  unidades: readonly UnidadVenta[],
  precios: readonly { id: string; precio: Centavos }[],
  listas: readonly Pick<ListaPrecio, 'id'>[],
  nombres: {
    producto: (p: Producto) => string;
    categoria: (categoria: string) => string;
    presentacion: (u: UnidadVenta, unidadBase: string) => string;
    locale: string;
  },
  conOfertas?: OfertasDelCatalogo,
): GrupoDelCatalogo[] {
  if (listas.length === 0) return [];
  const precioPor = new Map(precios.map((x) => [x.id, x.precio]));
  const porCategoria = new Map<string, FilaDelCatalogo[][]>();
  for (const p of productos) {
    if (!p.activo) continue;
    const producto = nombres.producto(p);
    const filas = unidadesDe(unidades, p.id).flatMap((u) => {
      const suyos = listas.map((l) => precioPor.get(precioId(u.id, l.id)) ?? null);
      return suyos.some((x) => x !== null)
        ? [{ productoId: p.id, producto, presentacion: nombres.presentacion(u, p.unidadBase), precios: suyos, foto: p.foto ?? null }]
        : [];
    });
    if (filas.length === 0) continue;
    const categoria = nombres.categoria(p.categoria);
    const grupo = porCategoria.get(categoria);
    if (grupo) grupo.push(filas);
    else porCategoria.set(categoria, [filas]);
  }
  const enOrden = (a: string, b: string) => a.localeCompare(b, nombres.locale);
  const grupos = [...porCategoria]
    .sort(([a], [b]) => enOrden(a, b))
    .map(([categoria, deCadaUno]) => ({
      categoria,
      filas: deCadaUno.sort((a, b) => enOrden(a[0]!.producto, b[0]!.producto)).flat(),
    }));
  const ofertas = conOfertas ? filasDeOfertas(productos, unidades, precioPor, listas, nombres, conOfertas) : [];
  return ofertas.length > 0 ? [{ categoria: conOfertas!.titulo, filas: ofertas }, ...grupos] : grupos;
}

/** The rows of the offers running on one of `listas`, by product name, each product's presentations smallest first. */
function filasDeOfertas(
  productos: readonly Producto[],
  unidades: readonly UnidadVenta[],
  precioPor: ReadonlyMap<string, Centavos>,
  listas: readonly Pick<ListaPrecio, 'id'>[],
  nombres: { producto: (p: Producto) => string; presentacion: (u: UnidadVenta, unidadBase: string) => string; locale: string },
  d: OfertasDelCatalogo,
): FilaDelCatalogo[] {
  const porId = new Map(productos.map((p) => [p.id, p]));
  const columna = new Map(listas.map((l, i) => [l.id, i]));
  const deCadaUno: FilaDelCatalogo[][] = [];
  for (const o of d.ofertas) {
    const p = porId.get(o.productoId);
    const i = columna.get(o.listaId);
    if (!p?.activo || i === undefined || estadoDeOferta(o, d.hoy) !== 'corre') continue;
    const suyas = new Map(o.precios.map((x) => [x.unidadVentaId, x]));
    const producto = nombres.producto(p);
    const hasta = ultimoDiaDeOferta(o);
    const filas = unidadesDe(unidades, p.id).flatMap((u) => {
      const suya = suyas.get(u.id);
      if (suya === undefined) return [];
      const antes = precioPor.get(precioId(u.id, o.listaId)) ?? null;
      // So many for fewer: at the list's price, the deal said under the name; none without a price.
      if ('lleva' in suya && antes === null) return [];
      const precio = 'lleva' in suya ? antes! : suya.precio;
      const nota = 'lleva' in suya ? d.notaLleva(suya.lleva, suya.paga, (antes! * suya.paga) as Centavos, hasta) : d.nota(antes !== null && antes > precio ? antes : null, hasta);
      return [
        {
          productoId: p.id,
          producto,
          presentacion: nombres.presentacion(u, p.unidadBase),
          precios: listas.map((_, j) => (j === i ? precio : null)),
          foto: p.foto ?? null,
          nota,
        },
      ];
    });
    if (filas.length > 0) deCadaUno.push(filas);
  }
  return deCadaUno.sort((a, b) => a[0]!.producto.localeCompare(b[0]!.producto, nombres.locale)).flat();
}

/** What the PDF says in the shop's language, and the shop as it introduces itself. */
export interface TextosDelCatalogo {
  negocio: string;
  /** Under the name: the address and the phone, where the shop has them. */
  contacto: string;
  titulo: string;
  fecha: string;
  producto: string;
  presentacion: string;
  /** The lists' names, in the order of each row's prices. */
  listas: readonly string[];
  /** A category carried over to the next page. */
  continua: (categoria: string) => string;
  pagina: (n: number, de: number) => string;
}

const MARGEN = 40;
const ARRIBA = 40;
/** Where what is listed must end, above the foot of the page. */
const ABAJO = 52;
const COLUMNA_PRECIO = 76;
const COLUMNA_PRESENTACION = 104;
const ALTO_CABECERA = 18;
const ALTO_CATEGORIA = 22;
const ALTO_FILA = 15;
/** A row with a note under its name: an offer's. */
const ALTO_FILA_CON_NOTA = 25;
const LETRA = 9;
const LETRA_NOTA = 7.5;
/** A product's photo: a square half an inch a side, beside its rows, with room round it. */
const LADO_FOTO = 36;
const COLUMNA_FOTO = LADO_FOTO + 8;
/** The least room a product with a photo takes, however few its rows. */
const ALTO_CON_FOTO = LADO_FOTO + 6;

/** A product's rows, together as `gruposDelCatalogo` lists them: they go on one page. */
function porProducto(filas: readonly FilaDelCatalogo[]): FilaDelCatalogo[][] {
  const bloques: FilaDelCatalogo[][] = [];
  for (const f of filas) {
    const ultimo = bloques.at(-1);
    if (ultimo && ultimo[0]!.productoId === f.productoId) ultimo.push(f);
    else bloques.push([f]);
  }
  return bloques;
}

/**
 * The catalogue on US Letter: the shop's name, what this is and the day on the first page; on
 * every page the columns' names, then each category under its name and its rows, a category that
 * does not end on a page carried over under its name again, and at the foot the shop, the day and
 * «Página 2 de 5». A category's name never ends a page without a row of it under it, and a
 * product's rows are never split between two pages.
 *
 * `fotos`, the products' photos by name, as JPEG: each beside its product's rows, which stand in the
 * middle of the room it takes. With none to draw there is no column for them, and the list is as
 * it was before photos.
 */
export function catalogoEnPdf(
  grupos: readonly GrupoDelCatalogo[],
  textos: TextosDelCatalogo,
  fotos: ReadonlyMap<string, Jpeg> = new Map(),
): DocumentoPdf {
  const pdf = new DocumentoPdf();
  const { ancho, alto } = pdf;
  const derecha = ancho - MARGEN;
  // Narrower price columns past three lists, so the names keep some room.
  const columnaPrecio = Math.min(COLUMNA_PRECIO, (derecha - MARGEN - COLUMNA_PRESENTACION - 150) / Math.max(1, textos.listas.length));
  /** Where each list's price ends, the last at the right margin. */
  const finDePrecio = textos.listas.map((_, i) => derecha - (textos.listas.length - 1 - i) * columnaPrecio);
  const presentacionX = derecha - textos.listas.length * columnaPrecio - COLUMNA_PRESENTACION;
  /** The photo of a row's product, when there is one to draw. */
  const fotoDe = (f: FilaDelCatalogo) => (f.foto ? fotos.get(f.foto) : undefined);
  const conFotos = grupos.some((g) => g.filas.some(fotoDe));
  /** Where the names start: after the photos' column, when there is one. */
  const productoX = MARGEN + 6 + (conFotos ? COLUMNA_FOTO : 0);
  const anchoProducto = presentacionX - productoX - 6;
  const altoFila = (f: FilaDelCatalogo) => (f.nota ? ALTO_FILA_CON_NOTA : ALTO_FILA);
  const altoFilas = (filas: readonly FilaDelCatalogo[]) => filas.reduce((a, f) => a + altoFila(f), 0);
  const altoDe = (filas: readonly FilaDelCatalogo[]) => Math.max(altoFilas(filas), fotoDe(filas[0]!) ? ALTO_CON_FOTO : 0);
  let y = 0;

  function cabeceraDeColumnas() {
    pdf.banda(MARGEN, y, derecha - MARGEN, ALTO_CABECERA, 0.92);
    const base = y + 12;
    const como = { fuente: 'negrita' as const, tamano: 8, gris: 0.25 };
    pdf.texto(productoX, base, textos.producto, como);
    pdf.texto(presentacionX, base, textos.presentacion, como);
    textos.listas.forEach((lista, i) =>
      pdf.texto(finDePrecio[i]! - 6, base, recortar(lista, columnaPrecio - 10, 'negrita', 8), { ...como, alinear: 'derecha' }),
    );
    y += ALTO_CABECERA;
  }

  function nuevaPagina() {
    pdf.nuevaPagina();
    y = ARRIBA;
    cabeceraDeColumnas();
  }

  function categoria(nombre: string) {
    const base = y + 15;
    pdf.texto(MARGEN + 6, base, recortar(nombre, derecha - MARGEN - 12, 'negrita', 10), { fuente: 'negrita', tamano: 10 });
    pdf.linea(MARGEN, y + ALTO_CATEGORIA - 2, derecha, y + ALTO_CATEGORIA - 2, { grosor: 0.8, gris: 0.35 });
    y += ALTO_CATEGORIA;
  }

  /** A product: its photo, and each of its rows with a line under it. */
  function bloque(filas: readonly FilaDelCatalogo[]) {
    const foto = fotoDe(filas[0]!);
    const altura = altoDe(filas);
    if (foto) pdf.imagen(MARGEN + 4, y + (altura - LADO_FOTO) / 2, LADO_FOTO, LADO_FOTO, filas[0]!.foto!, foto);
    let arriba = y + (altura - altoFilas(filas)) / 2;
    filas.forEach((f, i) => {
      const base = arriba + 10.5;
      pdf.texto(productoX, base, recortar(f.producto, anchoProducto, 'normal', LETRA), { tamano: LETRA });
      if (f.nota) pdf.texto(productoX, base + 10, recortar(f.nota, anchoProducto, 'normal', LETRA_NOTA), { tamano: LETRA_NOTA, gris: 0.4 });
      pdf.texto(presentacionX, base, recortar(f.presentacion, COLUMNA_PRESENTACION - 8, 'normal', LETRA), {
        tamano: LETRA,
        gris: 0.35,
      });
      f.precios.forEach((precio, j) =>
        pdf.texto(finDePrecio[j]! - 6, base, precio === null ? '—' : formatMoney(precio), {
          tamano: LETRA,
          gris: precio === null ? 0.6 : 0,
          alinear: 'derecha',
        }),
      );
      // Between a product's own rows, the line leaves its photo whole.
      if (i < filas.length - 1) pdf.linea(foto ? productoX - 6 : MARGEN, arriba + altoFila(f), derecha, arriba + altoFila(f), { grosor: 0.4, gris: 0.86 });
      arriba += altoFila(f);
    });
    pdf.linea(MARGEN, y + altura, derecha, y + altura, { grosor: 0.4, gris: 0.86 });
    y += altura;
  }

  // The first page: who, what and when, over the columns.
  pdf.nuevaPagina();
  y = ARRIBA;
  pdf.texto(MARGEN, y + 18, recortar(textos.negocio, derecha - MARGEN, 'negrita', 18), { fuente: 'negrita', tamano: 18 });
  y += 36;
  pdf.texto(MARGEN, y, `${textos.titulo} · ${textos.fecha}`, { tamano: 10, gris: 0.3 });
  if (textos.contacto) {
    y += 14;
    pdf.texto(MARGEN, y, recortar(textos.contacto, derecha - MARGEN, 'normal', 9), { tamano: 9, gris: 0.45 });
  }
  y += 16;
  cabeceraDeColumnas();

  const cabe = (cuanto: number) => y + cuanto <= alto - ABAJO;
  for (const g of grupos) {
    const bloques = porProducto(g.filas);
    if (bloques.length === 0) continue;
    if (!cabe(ALTO_CATEGORIA + altoDe(bloques[0]!))) nuevaPagina();
    categoria(g.categoria);
    for (const b of bloques) {
      if (!cabe(altoDe(b))) {
        nuevaPagina();
        categoria(textos.continua(g.categoria));
      }
      bloque(b);
    }
  }

  // The foot of each page, now that it is known how many there are.
  const pie = `${textos.negocio} · ${textos.titulo} · ${textos.fecha}`;
  for (let i = 0; i < pdf.cuantasPaginas; i++) {
    pdf.irAPagina(i);
    pdf.linea(MARGEN, alto - 34, derecha, alto - 34, { grosor: 0.4, gris: 0.8 });
    pdf.texto(MARGEN, alto - 22, recortar(pie, derecha - MARGEN - 90, 'normal', 8), { tamano: 8, gris: 0.45 });
    pdf.texto(derecha, alto - 22, textos.pagina(i + 1, pdf.cuantasPaginas), { tamano: 8, gris: 0.45, alinear: 'derecha' });
  }
  return pdf;
}
